import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  inject,
  input,
  model,
  OnInit,
  output,
  signal
} from '@angular/core';
import {AbstractControl, FormGroup} from '@angular/forms';
import {
  EnrichRotterdamHarbourDuesDeclaration,
  HarbourDuesDeclaration,
  HarbourDuesDeclarationStatus,
  HarbourDuesRotterdamResponse,
  HarbourDuesShippingCompany,
  Visit
} from '@portbase/bezoekschip-service-typescriptmodels';
import {HarbourDuesDeclarationTab} from '../../types';
import {HarbourDuesPermissionService} from "../../services/harbour-dues-permission.service";
import {sendCommandAndWait} from "../../../../common/utils";
import {VisitContext} from "../../../visit-context";
import {rxResource, takeUntilDestroyed} from "@angular/core/rxjs-interop";

@Component({
  selector: 'app-harbour-dues-rotterdam-details-tab',
  templateUrl: './harbour-dues-rotterdam-details.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class HarbourDuesDetailsRotterdamComponent implements OnInit {
  private readonly permissionService = inject(HarbourDuesPermissionService);
  private readonly destroyRef = inject(DestroyRef);

  readonly form = input.required<FormGroup>();
  readonly showValidationErrors = input(false);
  readonly isAdmin = input(false);
  readonly isHarbourDuesAdvisor = input(false);
  readonly declarationTabs = input<HarbourDuesDeclarationTab[]>([]);
  readonly selectedDeclaration = input.required<HarbourDuesDeclaration>();
  readonly visit = input.required<Visit>();
  readonly declarationSelected = input(false);
  readonly hasBerthVisits = input(false);
  readonly vesselDeadWeightKg = input<number>();

  readonly proFormaRequested = output<void>();

  readonly validProFormaDisplayed = model<boolean>();

  readonly canEdit = computed(() =>
    this.permissionService.canEdit(this.selectedDeclaration())
  );

  // The selected shipping company's SAP HBR business partner id, or null when none is selected.
  // Drives (re-)fetching the declaration details so the liner services are re-enriched for it.
  private readonly selectedBusinessPartnerId = signal<string | null>(null);

  // Always runs on init (business partner id null -> backend enriches from the visit carrier) and
  // re-runs whenever the selected shipping company changes.
  // BSHD-342 Phase 7: enrich is a COMMAND now (it does a BTP call and records the returned status),
  // dispatched via sendCommandAndWait so it is never query-cached — each (re)enrichment must hit BTP
  // afresh. The response payload still drives the resource exactly as before.
  private readonly detailsResource = rxResource<HarbourDuesRotterdamResponse, string | null>({
    params: () => this.selectedBusinessPartnerId(),
    stream: ({params}) => sendCommandAndWait(
      "com.portbase.bezoekschip.common.api.harbourdues.commands.EnrichRotterdamHarbourDuesDeclaration",
      <EnrichRotterdamHarbourDuesDeclaration>{
        crn: VisitContext.visit.crn,
        businessPartnerIdSapHbr: params
      })
  });

  readonly harbourDuesDetails = this.detailsResource.value;

  ngOnInit(): void {
    const shippingCompany = this.form().get('discount.rotterdam.shippingCompany') as AbstractControl | null;
    // Seed first, unconditionally: even when the control is absent, the first query must carry the
    // visit carrier's business partner id (the Discount panel - which preselects the carrier - is
    // not rendered on first open, so the carrier fallback cannot rely on it).
    this.selectedBusinessPartnerId.set(
      this.resolveBusinessPartnerId((shippingCompany?.value ?? null) as HarbourDuesShippingCompany | null));
    if (!shippingCompany) {
      return;
    }
    shippingCompany.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((value: HarbourDuesShippingCompany | null) =>
        this.selectedBusinessPartnerId.set(this.resolveBusinessPartnerId(value)));
  }

  // The single source of truth for which business partner id drives the details query:
  // the form value wins, else the visit carrier is used, else null.
  private resolveBusinessPartnerId(shippingCompany: HarbourDuesShippingCompany | null): string | null {
    return shippingCompany?.businessPartnerIdSapHbr
      ?? VisitContext.visit?.visitDeclaration?.arrivalVoyage?.carrier?.businessPartnerIdSapHbr
      ?? null;
  }

  readonly isAccepted = computed(
    () => this.selectedDeclaration()?.status === HarbourDuesDeclarationStatus.ACCEPTED
  );

  readonly isDeclared = computed(
    () => this.selectedDeclaration()?.status === HarbourDuesDeclarationStatus.DECLARED
  );

  protected readonly hasCurrentVersionInvoice = computed(() => {
    const declaration = this.selectedDeclaration();
    return (
      declaration?.invoices?.some(
        i => i.statementVersion === declaration.statementVersion
      ) || false
    );
  });
}
